import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import Stepper from "../components/StepperComponent";
import PricingComponent from "../components/BookingFlow/PricingComponent";
import PaymentComponent from "../components/BookingFlow/PaymentComponent";
import BookingConfirmComponent from "../components/BookingConfirmComponent";
import BookingFormComponent from "../components/BookingFlow/BookingFormComponent";

const BookingFlow: React.FC = () => {
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [steps, setSteps] = useState<number[]>([1, 2, 3, 4]);
  const totalSteps = steps.length;
  //const [formData, setFormData] = useState<any>({}); // Store data from all steps
  const location = useLocation();
  const { numAdults, numChilds, hotel, totalCost, hold, bookingResponse, email, date } = location.state;
  const [canProceed, setCanProceed] = useState<boolean>(false);
  const [formData, setFormData] = useState<any>(location.state?.formData || {});

  const SESSION_TIMEOUT_MINUTES = 10;
  const SESSION_TIMEOUT_MS = SESSION_TIMEOUT_MINUTES * 60 * 1000;
  const [secondsLeft, setSecondsLeft] = useState(SESSION_TIMEOUT_MS / 1000);

  // Initialize data from GuestInfoForm
  React.useEffect(() => {
    if (location.state) {
      setFormData(location.state);
    }
  }, [location.state]);

useEffect(() => {
  const countdownInterval = setInterval(() => {
    setSecondsLeft((prev) => {
      if (prev <= 1) {
        clearInterval(countdownInterval);
        alert("Your booking session has expired. Please start over.");
        window.history.back(); // Go to previous page
        return 0;
      }
      return prev - 1;
    });
  }, 1000);

  return () => clearInterval(countdownInterval); // Cleanup on unmount
}, []);

  useEffect(() => {
    const handleBeforeUnload = () => {
      localStorage.removeItem("passengers");
      localStorage.removeItem("formSubmitted");

      localStorage.removeItem("cardHolderName");
      localStorage.removeItem("email");
      localStorage.removeItem("phone");
      localStorage.removeItem("billingDetails");
      localStorage.removeItem("isPaymentSuccess");
    };

    // Clear local storage on page load
    handleBeforeUnload();

    // Attach event listener to clear local storage on page refresh
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      // Remove the event listener on component unmount
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    // Wait until the next paint cycle to scroll
    setTimeout(() => {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }, 0);
  }, [currentStep]);
  

  useEffect(() => {
    if (currentStep === 1) {
      setCanProceed(!!formData.selectedPlan); // Enable if a plan is selected
    } else if (currentStep === 2) {
      setCanProceed(!!formData.formSubmitted); // Enable if the form is submitted
    } else if (currentStep === 3) {
      setCanProceed(!!formData.isPaymentSuccess); // Enable if bookingResponse exists
    } else {
      setCanProceed(false); // Default to false for other steps
    }
  }, [currentStep, formData]);

  useEffect(() => {
    if (hold === 'hold') {
      setSteps([1, 2, 3]);
    } else if (formData?.selectedPlan === "Free Fare Hold") {
      setSteps([1, 2, 3]); // Reduce to 3 steps
    } else if (hold === "paid-deposit") {
      setSteps([1, 2]); // Reduce to 2 steps
    } else {
      setSteps([1, 2, 3, 4]); // Default to 4 steps
    }
  }, [formData?.selectedPlan]);

  const updateFormData = (data: any) => {
    setFormData((prevData: any) => ({ ...prevData, ...data }));

    // Dynamically recalculate canProceed based on the current step
    if (currentStep === 1 && data.selectedPlan) {
      setCanProceed(true);
    } else if (currentStep === 2 && data.formSubmitted) {
      setCanProceed(true);
    } else if (currentStep === 3 && data.isPaymentSuccess) {
      setCanProceed(true);
    } else {
      setCanProceed(false);
    }
  };

  const goToNextStep = () => {
    setCurrentStep((prev) => Math.min(prev + 1, steps.length));
  };

  const goToPreviousStep = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };


  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        if (hold === 'paid-deposit') {
          return (
            <PaymentComponent
              formData={formData}
              responseToken={bookingResponse || ""}
              onUpdate={updateFormData}
              onNext={goToNextStep}
              onBack={goToPreviousStep}
              email={email}
            />
          );
        }
        if (hold !== 'paid-deposit') {
          return (
            <PricingComponent
              formData={formData}
              onUpdate={updateFormData} // Update formData without navigating
              onNext={goToNextStep} // Only navigate when "Next" is explicitly clicked
              totalCost={totalCost}
              adultCount={numAdults}
              childCount={numChilds}
              hotel={hotel}
              hold={hold}
              date={date}
            />
          );
        }
        break;
      case 2:
        if (hold === 'paid-deposit') {
          return (
            <BookingConfirmComponent
              updatedDetailsFromDb={formData.bookingFromDB}
              // booking={formData.details}
              //bookingFromDB={formData.detailsFromDb}
            />
          );
        }
        if (hold != 'hold') {
          return (
            <BookingFormComponent
              formData={formData}
              onUpdate={updateFormData} // Update formData without navigating
              onNext={goToNextStep} // Only navigate when "Next" is explicitly clicked
              onBack={goToPreviousStep}
            />
          );
        }
        return (
          <PaymentComponent
            formData={formData}
            responseToken={bookingResponse || ""}
            onUpdate={updateFormData}
            onNext={goToNextStep}
            onBack={goToPreviousStep}
            email={email}
          />
        );
      case 3:
        if (steps.length === 3) {
          // Final step for "Free Fare Hold"
          return (
            <BookingConfirmComponent
            updatedDetailsFromDb={formData.bookingFromDB}
              // booking={formData.details}
              // bookingFromDB={formData.detailsFromDb}
            />
          );
        }
        return (
          <PaymentComponent
            formData={formData}
            responseToken={formData.bookingResponse?.responseToken || ""}
            onUpdate={updateFormData}
            onNext={goToNextStep}
            onBack={goToPreviousStep}
          />
        );
      case 4:
        return (
          <BookingConfirmComponent
          updatedDetailsFromDb={formData.bookingFromDB}
            // booking={formData.details}
            // bookingFromDB={formData.detailsFromDb}
          />
        );
      default:
        return <div>Error: Unknown Step</div>;
    }
  };

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="text-gray-600 font-semibold text-center mt-4">
        Booking session expires in: {Math.floor(secondsLeft / 60)}:
        {secondsLeft % 60 < 10 ? "0" : ""}
        {secondsLeft % 60} minutes
      </div>

      {/* Stepper Component */}
      <Stepper
        currentStep={currentStep}
        steps={steps}
        labels={
          steps.length === 3
            ? ["Pricing", "Booking Details", "Confirmation"]
            : ["Pricing", "Booking Details", "Payment", "Confirmation"]
        }
      />

      {/* Render the Current Step */}
      <div className="mt-8">{renderStepContent()}</div>

      {/* Navigation Buttons */}
      <div
        className={`flex mt-6 space-x-4 p-4 md:p-0 ${
          currentStep === 1 ? "justify-end" : "justify-between"
        }`}
      >
        {currentStep > 1 && currentStep < totalSteps && (
          <button
            className="px-6 py-3 w-full md:w-1/12 bg-[#e8f1ff] text-[#116FFF] font-semibold rounded-lg hover:bg-gradient-to-r hover:from-[#0065ff] hover:to-[#0047b3] hover:text-white transition duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-[#116FFF] disabled:opacity-50"
            onClick={goToPreviousStep}
          >
            Back
          </button>
        )}
        {currentStep < totalSteps && (
          <button
            className={`px-6 py-3 rounded-lg font-semibold transition duration-200 ease-in-out w-full md:w-1/12 ${
              canProceed
                ? "bg-gradient-to-r from-[#0047b3] to-[#0065ff] text-white hover:bg-gradient-to-r hover:from-[#0065ff] hover:to-[#0047b3]"
                : "bg-gray-300 text-white cursor-not-allowed"
            }`}
            onClick={canProceed ? goToNextStep : undefined}
            disabled={!canProceed}
          >
            Next
          </button>
        )}
      </div>
    </div>
  );
};

export default BookingFlow;
